package ui.app.assign.detail.definition

import androidx.compose.foundation.layout.*
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import api.ResourceType
import compose.assets.Svg
import compose.assets.svg.Edit
import org.jetbrains.compose.resources.StringResource
import ssoft.frontend.generated.resources.*
import tools.EFieldRegularInt
import tools.EFieldRegularLong
import tools.GlobalObjectShare
import tools.rememberString
import ui.app.assign.detail.AppDetailScope
import ui.resource.ResourceListingArgs
import ui.widget.LazyImage
import ui.widget.Space
import kotlin.time.Duration.Companion.milliseconds
import kotlin.time.Duration.Companion.minutes
import kotlin.time.Duration.Companion.seconds


@Composable
fun AppDetailScope.AppDefOfVideoAlphaLoop(
    wrapper: AppDetailScope.Wrapper,
    nnvl: AppDetailScope.WrapperDefinition.OfVideoAlphaLoop
) {

    if (display.isMobile) {
        Column {
            DefaultFields(
                nnvl = wrapper,
                modifier = Modifier.fillMaxWidth(),
                mainContent = {
                    VideoPicker(
                        nnvl = nnvl,
                        wrapper = wrapper,
                        modifier = Modifier.fillMaxWidth(),
                    )
                    Space(16.dp)
                },
                secondaryContent = {
                    RepeatsField(
                        wrapper = wrapper,
                        label = Res.string.layer_bottom_detail__video_alpha_repeats_label,
                        field = nnvl.repeats,
                        modifier = Modifier.fillMaxWidth()
                    )
                    Space(16.dp)
                    DelayField(
                        wrapper = wrapper,
                        label = Res.string.layer_bottom_detail__video_alpha_delay_before_start_label,
                        field = nnvl.delayStart,
                        modifier = Modifier.fillMaxWidth()
                    )
                    Space(16.dp)
                    DelayField(
                        wrapper = wrapper,
                        label = Res.string.layer_bottom_detail__video_alpha_delay_after_end_label,
                        field = nnvl.delayEnd,
                        modifier = Modifier.fillMaxWidth()
                    )
                    Space(24.dp)
                })
        }
    } else {
        Row {
            Column(Modifier.weight(1F)) {
                DefaultFields(wrapper, Modifier.fillMaxWidth())
            }
            Space(16.dp)
            Column(Modifier.weight(1F)) {
                VideoPicker(nnvl, wrapper, Modifier.fillMaxWidth())

                Space(16.dp)
                RepeatsField(
                    wrapper = wrapper,
                    label = Res.string.layer_bottom_detail__video_alpha_repeats_label,
                    field = nnvl.repeats,
                    modifier = Modifier.fillMaxWidth()
                )
                Space(16.dp)
                DelayField(
                    wrapper = wrapper,
                    label = Res.string.layer_bottom_detail__video_alpha_delay_before_start_label,
                    field = nnvl.delayStart,
                    modifier = Modifier.fillMaxWidth()
                )
                Space(16.dp)
                DelayField(
                    wrapper = wrapper,
                    label = Res.string.layer_bottom_detail__video_alpha_delay_after_end_label,
                    field = nnvl.delayEnd,
                    modifier = Modifier.fillMaxWidth()
                )
                Space(24.dp)
            }
        }
    }
    Space(64.dp)
}


@Composable
private fun AppDetailScope.VideoPicker(
    nnvl: AppDetailScope.WrapperDefinition.OfVideoAlphaLoop,
    wrapper: AppDetailScope.Wrapper,
    modifier: Modifier = Modifier.width(256.dp)
) {
    val imagePickerKey = remember(args.appId) {
        "VideoPicker:${args.appId}"
    }

    GlobalObjectShare.Consumer(imagePickerKey)

    Card(modifier) {
        Box {
            Column(Modifier.padding(16.dp)) {
                Text(Res.string.layer_bottom_detail__video_alpha_pick_label.rememberString())
                Space(8.dp)
                Card(Modifier.fillMaxWidth()) {
                    LazyImage(
                        id = nnvl.video.state,
                        modifier = Modifier.fillMaxWidth(),
                        contentScale = ContentScale.FillWidth
                    )
                }
            }
            if (wrapper.canEdit) {
                FloatingActionButton(
                    onClick = {
                        GlobalObjectShare.launchImagePicker(
                            key = imagePickerKey,
                            nav = nav,
                            args = ResourceListingArgs(
                                companyId = args.companyId,
                                restrictionType = ResourceType.VIDEO_ALPHA.mask,
                                restrictionCanIgnoreAspectRatio = true
                            )
                        ) {
                            it.firstOrNull()?.also { item ->
                                nnvl.video.set(item.id)
                            }
                        }
                    },
                    modifier = Modifier.align(Alignment.BottomEnd)
                ) {
                    Icon(
                        imageVector = Svg.Edit,
                        contentDescription = null
                    )
                }
            }
        }
    }
}


private val MINUTE_5 by lazy {
    5.minutes.inWholeMilliseconds.toFloat()
}

@Composable
private fun AppDetailScope.DelayField(
    wrapper: AppDetailScope.Wrapper,
    label: StringResource,
    field: EFieldRegularLong,
    modifier: Modifier = Modifier.width(256.dp)
) {

    Card(modifier = modifier) {
        Column(
            modifier = Modifier.padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Row {
                Text(
                    text = label.rememberString(),
                    modifier = Modifier.weight(1F)
                )
                Text(
                    text = field.state.milliseconds.inWholeSeconds.seconds.toString().let {
                        if (it.last() == 'm') "$it 00s" else it
                    },
                    modifier = Modifier.align(Alignment.CenterVertically),
                    fontWeight = FontWeight.Bold
                )
            }
            Slider(
                enabled = wrapper.canEdit,
                value = (field.state.toFloat() / MINUTE_5),
                steps = 29,
                onValueChange = {
                    field.set((it.toFloat() * MINUTE_5).toLong())
                }
            )
        }
    }
}

@Composable
private fun AppDetailScope.RepeatsField(
    wrapper: AppDetailScope.Wrapper,
    label: StringResource,
    field: EFieldRegularInt,
    modifier: Modifier = Modifier.width(256.dp)
) {

    Card(modifier = modifier) {
        Column(
            modifier = Modifier.padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Row {
                Text(
                    text = label.rememberString(),
                    modifier = Modifier.weight(1F)
                )
                Text(
                    text = field.state.takeIf { it > 0 }?.toString() ?: "∞",
                    textAlign = TextAlign.Center,
                    modifier = Modifier.align(Alignment.CenterVertically),
                    fontWeight = FontWeight.Bold
                )
            }
            Slider(
                enabled = wrapper.canEdit,
                value = (field.state.toFloat() / 100F),

                onValueChange = {
                    field.set((it.toFloat() * 100F).toInt())
                }
            )
        }
    }
}